import type { StreetNumber } from '@gov-design-system-ce/react-form';
import { streetNumberSchema } from '@gov-design-system-ce/react-form';
import * as yup from 'yup';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import type { StreetNumbersInputProps } from '@gov-nx/ui/web';
import type { RequiredWhenFieldCondition } from '@gov-nx/utils/common';
import { requiredWhenAll } from '@gov-nx/utils/common';

export interface StreetNumbersFormDefinitions {
	schema: ReturnType<typeof streetNumberSchema>;
	defaultValue: StreetNumber;
	field: StreetNumbersInputProps;
}

export const useStreetNumbersFormDefinitions = ({
	name,
	isRequiredWhenAll,
}: {
	name: string;
	isRequiredWhenAll?: { conditions: RequiredWhenFieldCondition[] };
}): StreetNumbersFormDefinitions => {
	const { t } = useLocale(LocalizeNameSpaceTypes.Form);

	const fullSchema = streetNumberSchema({
		errorMessages: {
			notNegative: t('cislo-ulice.validace.chyba.kladne-cislo'),
			notLeadingZero: t('cislo-ulice.validace.chyba.zacina-nulou'),
			maxLength: t('cislo-ulice.validace.chyba.max-4-cisla'),
			notEmpty: t('cislo-ulice.validace.chyba.neuvedeno'),
		},
	});

	const schema = isRequiredWhenAll
		? requiredWhenAll(fullSchema, isRequiredWhenAll.conditions, (allMatch) =>
				allMatch
					? fullSchema
					: yup.object().shape({
							popisneCislo: yup.string().optional(),
							orientacniCislo: yup.string().optional(),
						})
			)
		: fullSchema;

	return {
		schema,
		defaultValue: {
			popisneCislo: '',
			orientacniCislo: '',
		},
		field: {
			field: {
				name,
				streetNumberPlaceholder: t(
					'cislo-ulice.placeholders.vychozi.cislo-orientacni'
				),
				descriptiveNumberPlaceholder: t(
					'cislo-ulice.placeholders.vychozi.cislo-popisne'
				),
			},
		},
	};
};
